import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Accordion from '../../components/common/Accordian';
import EditIcon from '../../assets/EditIcon';
import SunIcon from '../../assets/SunIcon';
import FlightCard from '../../components/common/FlightCard';
import UnallocatedCard from '../../components/common/UnallocatedCard';
import WarningIcon from '../../assets/WarningIcon';
import flightApi from '../../api/flightAllocation';
import FlightBookingDetailsCard from '../../components/common/FlightBookingsCard';
import {
  IFlightAllocation,
  IFlightBookingDetails,
} from '../../interfaces/flightBookingDetails';

import { DateSelector } from '../../components';
import { encodeDateForUrl, getAdjustedDate } from '../../utils/dateUtils';
import {
  calculatePaxPercentage,
  calculateTotalWeight,
  showOrReplaceToast,
} from '../../utils/utility';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import useTemperatureSecondStore from '../../store/temperatureSecondStore';
import Loader from '../../assets/loader/Loader';
import useLocationStore from '../../store/locatonStore';
import useDateStore from '../../store/selectedDateStore';
import { IOriginalAllocation } from '../../interfaces/originalFlightAllocation';
import { ISelectedFlightsData } from '../../interfaces/selectedFlightsData';
import useBgColorStore from '../../store/bgColor';
import CalendarHeader from '../../components/common/Header/CalenderHeader';
import SunIconDark from '../../assets/SunIconDark';
import EditIconDark from '../../assets/EditIconDark';
import WarningIconDark from '../../assets/WarningIconDark';
import ReusableSelect from '../../components/common/Select';
import { ITemperature } from '../../type/temperature';
import { IOperations } from '../../type/operation';
import {
  useOperationDetails,
  useTemperatureList,
  useTodayTemperature,
} from '../../hooks/apiHook/operationDetails';
import useTemperatureStore from '../../store/temperatureStore';
import operationAPI from '../../api/operationApi';
import { IAddTemperatureRequest } from '../../type/addTemp';

const FlightMobile = () => {
  const { selectedDateFromStore, setSelectedDateFromStore } = useDateStore();
  const { bgColor, updateBgColor } = useBgColorStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const [isUnallocatedVisible, setIsUnallocatedVisible] = useState(false);
  const [flightData, setFlightData] = useState<any>(null);
  const [unAllocatedPax, setUnAllocatedPax] = useState<any>(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [unAllocatedPaxPercentage, setUnAllocatedPaxPercentage] =
    useState<String>('0');
  const { selectedTemperature2 } = useTemperatureSecondStore();

  const [selectedFlight, setSelectedFlight] = useState<{
    [key: string]: ISelectedFlightsData;
  }>({});

  const [originalAllocations, setOriginalAllocations] = useState<{
    [bookingId: string]: IOriginalAllocation[];
  }>({});

  const [realTimeFlightPax, setRealTimeFlightPax] = useState<{
    [key: string]: number;
  }>({
    totalPax: 0,
    totalAllocatedPax: 0,
  });
  const [flightAllocations, setFlightAllocations] = useState<{
    [operationBalloonFlightId: string]: number;
  }>({});

  const [busAllocationsWeight, setBusAllocationsWeight] = useState<{
    [operationBalloonFlightId: string]: number;
  }>({});

  const [editModeMap, setEditModeMap] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [selectedDate, setSelectedDate] = useState<Date>(
    selectedDateFromStore || getAdjustedDate(),
  );
  const [isLoading, setIsLoading] = useState(false);

  const fetchFlightAllocationDetails = useCallback(
    async (date: Date, isShowLoader?: boolean) => {
      const encodedDate = encodeDateForUrl(date);
      try {
        setIsPageLoading(isShowLoader ?? false);
        const response = await flightApi.getFlightAllocationDetails(
          selectedLocation.OwnerId,
          encodedDate,
        );
        setFlightData(response?.data);
        const percentageNeeded = calculatePaxPercentage(
          response?.data?.totalPax,
          response?.data?.totalAllocatedPax,
        );
        setUnAllocatedPaxPercentage(percentageNeeded?.toFixed(0));

        // Initialize selectedBuses and originalAllocations
        const initialSelectedBuses: { [key: string]: ISelectedFlightsData } =
          {};
        const initialOriginalAllocations: {
          [key: string]: IOriginalAllocation[];
        } = {};

        response?.data?.bookings?.forEach((booking: IFlightBookingDetails) => {
          const bookingIdString = String(booking.bookingId);
          const zoneIdStr = String(booking.zoneId);
          const bookingProductTripIdStr = String(booking?.bookingProductTripId);
          const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

          // Initialize arrays if needed
          if (!initialOriginalAllocations[combinedKey]) {
            initialOriginalAllocations[combinedKey] = [];
          }

          if (!initialSelectedBuses[combinedKey]) {
            initialSelectedBuses[combinedKey] = {
              bookingProductTripId: booking.bookingProductTripId,
              bookingId: booking.bookingId,
              paxNo: booking.paxNo,
              allocNo: booking?.allocNo,
              zoneId: booking?.zoneId,
              allocations: [],
            };
          }

          // Handle array of allocations
          booking.flightAllocation?.forEach((allocation) => {
            if (allocation.operationBalloonFlightId) {
              // Add to original allocations
              initialOriginalAllocations[combinedKey].push({
                operationBalloonFlightId: allocation.operationBalloonFlightId,
                paxNo: Number(allocation.paxNo || booking.paxNo),
              });

              // Add to selected buses
              initialSelectedBuses[combinedKey].allocations.push({
                operationBalloonFlightId: allocation.operationBalloonFlightId,
                splitMaleNo: Number(allocation?.male),
                splitFemaleNo: Number(allocation?.female),
                splitChildNo: Number(allocation?.child),
                oldOperationBalloonFlightId:
                  allocation.operationBalloonFlightId,
                splitPax: Number(allocation.paxNo || booking.paxNo),
                isDeleted: false,
                maleWeight: booking?.maleWeight,
                femaleWeight: booking?.femaleWeight,
                childWeight: booking?.childWeight,
              });
            }
          });

          // If no allocations were found, remove the entry
          if (initialSelectedBuses[combinedKey].allocations.length === 0) {
            delete initialSelectedBuses[combinedKey];
          }
        });
        // set value
        setOriginalAllocations(initialOriginalAllocations);
        setSelectedFlight(initialSelectedBuses);
      } catch (error) {
      } finally {
        setIsPageLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchFlightAllocationDetails(selectedDate, true);
  }, [selectedDate, fetchFlightAllocationDetails]);

  const onIsUnallocatedVisible = async () => {
    await fetchFlightUnallocatedPax(selectedDate);
    setIsUnallocatedVisible(true);
  };
useEffect(() => {
    setSelectedDate(selectedDateFromStore);
  }, [selectedDateFromStore]);
  const fetchFlightUnallocatedPax = async (date: Date) => {
    // setLoading(true);
    try {
      const encodedDate = encodeDateForUrl(date);
      const response = await flightApi.getFlightUnAllocatedPaxDetails(
        selectedLocation.OwnerId,
        encodedDate,
      );
      setUnAllocatedPax(response?.data);
    } catch (error) {
    } finally {
      // setLoading(false);
    }
  };

  const allocatedFlight = async (booking: IFlightBookingDetails) => {
    try {
      //TODO: I will remove this in next PR
      console.log({ selectedFlight });
      console.log({ booking });
      const selectedFlightDetails =
        selectedFlight[
          `${booking?.bookingId}_${booking?.zoneId}_${booking?.bookingProductTripId}`
        ];
      if (!selectedFlightDetails) {
        toast.error(translate('flightAllocation.errorMessage.selectFlight'));
        return;
      }
      if (Object.keys(selectedFlightDetails).length === 0) return;

      const payload = (selectedFlightDetails?.allocations || [])
        .filter((allocation) => {
          const original =
            originalAllocations[selectedFlightDetails.bookingId] || [];
          const originalAlloc = original.find(
            (orig) =>
              String(orig.operationBalloonFlightId) ===
              String(allocation.operationBalloonFlightId),
          );

          return (
            allocation.isDeleted ||
            !originalAlloc ||
            originalAlloc.paxNo !== allocation.splitPax
          );
        })
        .filter((allocation) => {
          if (allocation.isDeleted) return true;

          // Filter out allocations with invalid pax counts
          return (
            (allocation.splitMaleNo !== undefined &&
              allocation.splitMaleNo !== null &&
              allocation.splitMaleNo > 0) ||
            (allocation.splitFemaleNo !== undefined &&
              allocation.splitFemaleNo !== null &&
              allocation.splitFemaleNo > 0) ||
            (allocation.splitChildNo !== undefined &&
              allocation.splitChildNo !== null &&
              allocation.splitChildNo > 0)
          );
        })
        .map((allocation) => {
          const bookingData = {
            maleWeight:
              allocation.splitMaleNo * Number(allocation.maleWeight || 0),
            male: allocation.splitMaleNo,
            femaleWeight:
              allocation.splitFemaleNo * Number(allocation.femaleWeight || 0),
            female: allocation.splitFemaleNo,
            childWeight:
              allocation.splitChildNo * Number(allocation.childWeight || 0),
            child: allocation.splitChildNo,
          };

          const totalWeight = calculateTotalWeight(bookingData);

          return {
            bookingProductTripId: booking.bookingProductTripId,
            bookingId: Number(booking.bookingId),
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            paxNo:
              allocation.splitMaleNo +
              allocation.splitFemaleNo +
              allocation.splitChildNo,

            maleNo: allocation.splitMaleNo | 0,
            femaleNo: allocation.splitFemaleNo | 0,
            childNo: allocation.splitChildNo | 0,

            weight: totalWeight,

            ...(allocation.oldOperationBalloonFlightId && {
              oldOperationBalloonFlightId:
                allocation.oldOperationBalloonFlightId,
            }),

            isDeleted: allocation.isDeleted || false,
          };
        });

      if (payload.length > 0) {
        setIsLoading(true);
        const response = await flightApi.addFlightPickupAllocation(payload);
        console.log('Response:', response?.data); //TODO:  I will remove this in next PR

        await fetchFlightAllocationDetails(selectedDate);
        setIsLoading(false);
        showOrReplaceToast(
          'operations',
          translate('flightAllocation.successMessage.allocationSave'),
          'success',
        );
        setEditModeMap((prev) => ({
          ...prev,
          [`${booking?.bookingId}_${booking?.bookingProductTripId}`]: false,
        }));
      } else {
        toast.error(translate('common.allocatePaxUpdate'));
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching allocation details:', error);
    }
  };

  useEffect(() => {
    if (flightData) {
      setRealTimeFlightPax({
        totalPax: flightData?.totalPax ?? 0,
        totalAllocatedPax: flightData?.totalAllocatedPax ?? 0,
      });

      // Initialize bus-specific allocation counts
      const initialBusAllocs: { [operationBalloonFlightId: string]: number } =
        {};
      const initialBusAllocsWeight: {
        [operationBalloonFlightId: string]: number;
      } = {};

      flightData?.flightList?.forEach((flight: any) => {
        initialBusAllocs[flight.operationBalloonFlightId] =
          flight.allocatedPax || 0;

        initialBusAllocsWeight[flight.operationBalloonFlightId] =
          flight.totalWeight || 0;
      });
      setFlightAllocations(initialBusAllocs);
      setBusAllocationsWeight(initialBusAllocsWeight);
    }
  }, [flightData]);

  //calculate and update allocation stats based on changes
  const updateAllocationStats = useCallback(() => {
    // Calculate bus-specific allocations
    const newBusAllocations: { [operationBalloonFlightId: string]: number } =
      {};

    const newBusAllocationsWeight: {
      [operationBalloonFlightId: string]: number;
    } = {};
    flightData?.flightList?.forEach((flight: any) => {
      newBusAllocations[flight.operationBalloonFlightId] = 0;
      newBusAllocationsWeight[flight.operationBalloonFlightId] = 0;
    });

    // Calculate total allocated pax and bus-specific allocations
    let totalAllocatedPax = 0;

    Object.values(selectedFlight).forEach((booking) => {
      booking?.allocations?.forEach((allocation) => {
        if (!allocation.isDeleted) {
          const busId = allocation.operationBalloonFlightId.toString();
          // const paxCount = allocation.splitPax;
          const maleNo = allocation.splitMaleNo
            ? Number(allocation.splitMaleNo)
            : 0;
          const femaleNo = allocation.splitFemaleNo
            ? Number(allocation.splitFemaleNo)
            : 0;
          const childNo = allocation.splitChildNo
            ? Number(allocation.splitChildNo)
            : 0;

          const paxCount = maleNo + femaleNo + childNo;
          const calculateWeightObj = {
            maleWeight: allocation?.maleWeight,
            male: maleNo,
            femaleWeight: allocation?.femaleWeight,
            female: femaleNo,
            childWeight: allocation?.childWeight,
            child: childNo,
          };
          const totalWeight = calculateTotalWeight(calculateWeightObj);

          if (newBusAllocations[busId] !== undefined) {
            newBusAllocations[busId] += paxCount;
            newBusAllocationsWeight[busId] += totalWeight;
            totalAllocatedPax += paxCount;
          }
        }
      });
    });

    // Include original allocations that are not in selectedFlight
    flightData?.bookings?.forEach((booking: any) => {
      // Skip if this booking is in our selectedFlight (already counted)
      if (
        selectedFlight[
          `${booking?.bookingId}_${booking?.zoneId}_${booking?.bookingProductTripId}`
        ]
      )
        return;

      // Add all original allocations from bookings not in selectedBuses
      booking.flightAllocation?.forEach((allocation: any) => {
        if (allocation.operationBalloonFlightId) {
          const busId = allocation.operationBalloonFlightId.toString();
          const maleCount = Number(allocation.male ?? booking.male);
          const femaleCount = Number(allocation.female ?? booking.female);
          const childCount = Number(allocation.child ?? booking.child);
          const paxCount = maleCount + femaleCount + childCount;

          const calculateWeightObj = {
            maleWeight: booking?.maleWeight,
            male: maleCount,
            femaleWeight: booking?.femaleWeight,
            female: femaleCount,
            childWeight: booking?.childWeight,
            child: childCount,
          };
          const totalWeight = calculateTotalWeight(calculateWeightObj);

          if (newBusAllocations[busId] !== undefined) {
            newBusAllocations[busId] += paxCount;
            newBusAllocationsWeight[busId] += totalWeight;
            totalAllocatedPax += paxCount;
          }
        }
      });
    });

    // Update the state with new allocations
    setFlightAllocations(newBusAllocations);
    setBusAllocationsWeight(newBusAllocationsWeight);

    setRealTimeFlightPax((prev) => ({
      ...prev,
      totalAllocatedPax,
    }));
  }, [selectedFlight, flightData]);

  // Call this function whenever selectedBuses changes
  useEffect(() => {
    updateAllocationStats();
  }, [selectedFlight, updateAllocationStats]); // TODO: isEditMode,

  const handleCheckboxChange = (
    bookingId: number,
    operationBalloonFlightId: number,
    bookingProductTripId: number,
    paxNo: number,
    maleNo: number,
    femaleNo: number,
    childNo: number,
    maleWeight: string | number,
    femaleWeight: string | number,
    childWeight: string | number,
    zoneId: number,
    oldOperationBalloonFlightId?: number | null,
  ) => {
    const bookingIdString = String(bookingId);
    const operationBalloonFlightIdStr = String(operationBalloonFlightId);
    const zoneIdStr = String(zoneId);
    const bookingProductTripIdStr = String(bookingProductTripId);
    const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

    setSelectedFlight((prev) => {
      const currentBooking = prev[combinedKey];
      // Check if this is an original allocation
      const isOriginalAllocation = originalAllocations[combinedKey]?.some(
        (orig) =>
          String(orig.operationBalloonFlightId) === operationBalloonFlightIdStr,
      );

      if (!currentBooking) {
        // First selection for this booking
        return {
          ...prev,
          [combinedKey]: {
            bookingProductTripId,
            bookingId,
            paxNo,
            allocNo: paxNo,
            allocations: [
              {
                operationBalloonFlightId,
                oldOperationBalloonFlightId,
                splitPax: Number(paxNo),
                splitMaleNo: Number(maleNo),
                splitFemaleNo: Number(femaleNo),
                splitChildNo: Number(childNo),
                maleWeight: maleWeight || 0,
                femaleWeight: femaleWeight || 0,
                childWeight: childWeight || 0,
                isDeleted: false,
              },
            ],
          },
        } as { [key: string]: ISelectedFlightsData };
      }

      // Check if this flight is already in allocations (active or deleted)
      const existingAllocationIndex = currentBooking.allocations.findIndex(
        (a) =>
          String(a.operationBalloonFlightId) === operationBalloonFlightIdStr,
      );

      // If the allocation exists but is marked as deleted, we need to reactivate it
      if (
        existingAllocationIndex !== -1 &&
        currentBooking.allocations[existingAllocationIndex].isDeleted
      ) {
        // This is a previously deleted allocation that should be reactivated
        const updatedAllocations = [...currentBooking.allocations];

        // Mark it as not deleted
        updatedAllocations[existingAllocationIndex] = {
          ...updatedAllocations[existingAllocationIndex],
          isDeleted: false,
        };

        // Recalculate pax counts for all active allocations
        const activeAllocations = updatedAllocations.filter(
          (a) => !a.isDeleted,
        );

        // Using even distribution of passengers
        const totalPax = Number(paxNo);
        const totalMale = Number(maleNo);
        const totalFemale = Number(femaleNo);
        const totalChild = Number(childNo);

        const paxPerAllocation = Math.floor(
          totalPax / activeAllocations.length,
        );
        const malePerAllocation = Math.floor(
          totalMale / activeAllocations.length,
        );
        const femalePerAllocation = Math.floor(
          totalFemale / activeAllocations.length,
        );
        const childPerAllocation = Math.floor(
          totalChild / activeAllocations.length,
        );

        const paxRemainder = totalPax % activeAllocations.length;
        const maleRemainder = totalMale % activeAllocations.length;
        const femaleRemainder = totalFemale % activeAllocations.length;
        const childRemainder = totalChild % activeAllocations.length;

        activeAllocations.forEach((alloc, idx) => {
          const allocIndex = updatedAllocations.findIndex((a) => a === alloc);
          if (allocIndex !== -1) {
            updatedAllocations[allocIndex] = {
              ...updatedAllocations[allocIndex],
              splitPax: paxPerAllocation + (idx < paxRemainder ? 1 : 0),
              splitMaleNo: malePerAllocation + (idx < maleRemainder ? 1 : 0),
              splitFemaleNo:
                femalePerAllocation + (idx < femaleRemainder ? 1 : 0),
              splitChildNo: childPerAllocation + (idx < childRemainder ? 1 : 0),
            };
          }
        });

        const totalAllocated = updatedAllocations.reduce((sum, alloc) => {
          if (alloc.isDeleted) return sum;
          const male = Number(alloc.splitMaleNo) || 0;
          const female = Number(alloc.splitFemaleNo) || 0;
          const child = Number(alloc.splitChildNo) || 0;
          return sum + male + female + child;
        }, 0);

        return {
          ...prev,
          [combinedKey]: {
            ...currentBooking,
            allocations: updatedAllocations,
            allocNo: totalAllocated,
          },
        };
      }

      // Normal checkbox handling for toggling a currently active allocation
      if (
        existingAllocationIndex !== -1 &&
        !currentBooking.allocations[existingAllocationIndex].isDeleted
      ) {
        // Bus is already selected - handle unselection
        if (isOriginalAllocation) {
          // For originally allocated buses, mark for deletion instead of removing
          const updatedAllocations = currentBooking.allocations.map(
            (alloc, index) =>
              index === existingAllocationIndex
                ? { ...alloc, isDeleted: true }
                : alloc,
          );

          // Get the counts from the allocation being deleted
          const paxToRedistribute =
            updatedAllocations[existingAllocationIndex].splitPax;
          const maleToRedistribute =
            updatedAllocations[existingAllocationIndex].splitMaleNo;
          const femaleToRedistribute =
            updatedAllocations[existingAllocationIndex].splitFemaleNo;
          const childToRedistribute =
            updatedAllocations[existingAllocationIndex].splitChildNo;

          // Find active allocations (not marked for deletion)
          const activeAllocations = updatedAllocations.filter(
            (a) => !a.isDeleted,
          );

          // Redistribute pax counts for active allocations
          if (activeAllocations.length > 0) {
            if (activeAllocations.length === 1) {
              // If only one active allocation remains, give it all passengers
              const allocIndex = updatedAllocations.findIndex(
                (a) => a === activeAllocations[0],
              );
              if (allocIndex !== -1) {
                updatedAllocations[allocIndex] = {
                  ...updatedAllocations[allocIndex],
                  splitPax: Number(paxNo),
                  splitMaleNo: Number(maleNo),
                  splitFemaleNo: Number(femaleNo),
                  splitChildNo: Number(childNo),
                };
              }
            } else {
              // Calculate distribution for multiple allocations
              const paxPerAllocation = Math.floor(
                paxToRedistribute / activeAllocations.length,
              );
              const malePerAllocation = Math.floor(
                maleToRedistribute / activeAllocations.length,
              );
              const femalePerAllocation = Math.floor(
                femaleToRedistribute / activeAllocations.length,
              );
              const childPerAllocation = Math.floor(
                childToRedistribute / activeAllocations.length,
              );

              const paxRemainder = paxToRedistribute % activeAllocations.length;
              const maleRemainder =
                maleToRedistribute % activeAllocations.length;
              const femaleRemainder =
                femaleToRedistribute % activeAllocations.length;
              const childRemainder =
                childToRedistribute % activeAllocations.length;

              // Reset the counts for all active allocations
              activeAllocations.forEach((alloc, idx) => {
                const allocIndex = updatedAllocations.findIndex(
                  (a) => a === alloc,
                );
                if (allocIndex !== -1) {
                  updatedAllocations[allocIndex] = {
                    ...updatedAllocations[allocIndex],
                    splitPax:
                      updatedAllocations[allocIndex].splitPax +
                      paxPerAllocation +
                      (idx === 0 ? paxRemainder : 0),
                    splitMaleNo:
                      updatedAllocations[allocIndex].splitMaleNo +
                      malePerAllocation +
                      (idx === 0 ? maleRemainder : 0),
                    splitFemaleNo:
                      updatedAllocations[allocIndex].splitFemaleNo +
                      femalePerAllocation +
                      (idx === 0 ? femaleRemainder : 0),
                    splitChildNo:
                      updatedAllocations[allocIndex].splitChildNo +
                      childPerAllocation +
                      (idx === 0 ? childRemainder : 0),
                  };
                }
              });
            }
          }

          // Calculate total allocated pax for the booking
          const totalAllocatedPax = updatedAllocations.reduce((sum, alloc) => {
            if (alloc.isDeleted) return sum;
            return sum + alloc.splitPax;
          }, 0);

          return {
            ...prev,
            [combinedKey]: {
              ...currentBooking,
              allocations: updatedAllocations,
              allocNo: totalAllocatedPax,
            },
          };
        } else {
          // For new allocations, remove them entirely
          const paxToRedistribute =
            currentBooking.allocations[existingAllocationIndex].splitPax;
          const newAllocations = currentBooking.allocations.filter(
            (_, index) => index !== existingAllocationIndex,
          );

          if (newAllocations.length === 0) {
            const newSelected = { ...prev };
            delete newSelected[combinedKey];
            return newSelected;
          }
          // Redistribute pax among remaining allocations
          if (newAllocations.length === 1) {
            // When only one flight remains selected, allocate ALL passengers to it
            newAllocations[0].splitPax = Number(paxNo);
            newAllocations[0].splitMaleNo = Number(maleNo);
            newAllocations[0].splitFemaleNo = Number(femaleNo);
            newAllocations[0].splitChildNo = Number(childNo);
          } else if (newAllocations.length > 1) {
            // For multiple flights, distribute passengers proportionally
            const paxPerAllocation = Math.floor(
              paxToRedistribute / newAllocations.length,
            );
            const remainder = paxToRedistribute % newAllocations.length;

            // Also distribute gender-specific counts proportionally
            const malePerAllocation = Math.floor(
              Number(maleNo) / newAllocations.length,
            );
            const femalePerAllocation = Math.floor(
              Number(femaleNo) / newAllocations.length,
            );
            const childPerAllocation = Math.floor(
              Number(childNo) / newAllocations.length,
            );

            const maleRemainder = Number(maleNo) % newAllocations.length;
            const femaleRemainder = Number(femaleNo) % newAllocations.length;
            const childRemainder = Number(childNo) % newAllocations.length;

            newAllocations.forEach((alloc, idx) => {
              alloc.splitPax += paxPerAllocation + (idx === 0 ? remainder : 0);
              alloc.splitMaleNo =
                malePerAllocation + (idx === 0 ? maleRemainder : 0);
              alloc.splitFemaleNo =
                femalePerAllocation + (idx === 0 ? femaleRemainder : 0);
              alloc.splitChildNo =
                childPerAllocation + (idx === 0 ? childRemainder : 0);
            });
          }

          // Calculate total allocated pax for the booking
          const totalAllocatedPax = newAllocations.reduce((sum, alloc) => {
            return sum + alloc.splitPax;
          }, 0);

          return {
            ...prev,
            [combinedKey]: {
              ...currentBooking,
              allocations: newAllocations,
              allocNo: totalAllocatedPax,
            },
          };
        }
      }

      // Add new balloon flight allocation
      const newAllocation = {
        operationBalloonFlightId,
        oldOperationBalloonFlightId: null, // Preserve the oldOperationBalloonFlightId if it exists
        splitPax: 0,
        splitMaleNo: 0,
        splitFemaleNo: 0,
        splitChildNo: 0,
        maleWeight: maleWeight || 0,
        femaleWeight: femaleWeight || 0,
        childWeight: childWeight || 0,
        isDeleted: false,
      };

      // Filter out the deleted allocations for calculation purposes
      const activeAllocations = currentBooking.allocations.filter(
        (a) => !a.isDeleted,
      );
      const newAllocations = [...currentBooking.allocations, newAllocation];

      // Calculate split pax
      if (activeAllocations.length === 0) {
        // If all previous allocations were deleted, give full pax to new allocation
        newAllocation.splitPax = Number(paxNo);
        newAllocation.splitMaleNo = Number(maleNo);
        newAllocation.splitFemaleNo = Number(femaleNo);
        newAllocation.splitChildNo = Number(childNo);
      } else {
        // Calculate the total pax already allocated to active allocations
        const allocatedPax = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitPax,
          0,
        );
        const allocatedMales = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitMaleNo,
          0,
        );
        const allocatedFemales = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitFemaleNo,
          0,
        );
        const allocatedChildren = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitChildNo,
          0,
        );

        // Available pax = total pax - allocated pax
        const availablePax = Number(paxNo) - allocatedPax;
        const availableMales = Number(maleNo) - allocatedMales;
        const availableFemales = Number(femaleNo) - allocatedFemales;
        const availableChildren = Number(childNo) - allocatedChildren;

        // Assign available pax to the new allocation
        newAllocation.splitPax = Math.max(0, availablePax);
        newAllocation.splitMaleNo = Math.max(0, availableMales);
        newAllocation.splitFemaleNo = Math.max(0, availableFemales);
        newAllocation.splitChildNo = Math.max(0, availableChildren);
      }

      return {
        ...prev,
        [combinedKey]: {
          ...currentBooking,
          allocations: newAllocations,
        },
      };
    });
  };

  // update  the toggleEditMode function to reset values when canceling edits
  const onToggleEditMode = (id: number, bookingProductTripId: number) => {
    // if (isEditMode) {
    const originalBusAllocs: { [operationBalloonFlightId: string]: number } =
      {};
    const originalBusAllocsWeight: {
      [operationBalloonFlightId: string]: number;
    } = {};

    flightData?.flightList?.forEach((flight: any) => {
      originalBusAllocs[flight.operationBalloonFlightId] =
        flight.allocatedPax || 0;
      originalBusAllocsWeight[flight.operationBalloonFlightId] =
        flight.totalWeight || 0;
    });
    setFlightAllocations(originalBusAllocs);
    setBusAllocationsWeight(originalBusAllocsWeight);

    // Restore original busPax count
    setRealTimeFlightPax({
      totalPax: flightData?.totalPax ?? 0,
      totalAllocatedPax: flightData?.totalAllocatedPax ?? 0,
    });
    // TODO: may be this is reason to reset value and update
    // Reset selectedFlight to original state
    const initialSelectedBuses: { [key: string]: ISelectedFlightsData } = {};
    const initialOriginalAllocations: {
      [key: string]: IOriginalAllocation[];
    } = {};

    flightData?.bookings?.forEach((booking: any) => {
      const bookingIdString = String(booking.bookingId);
      const zoneIdStr = String(booking?.zoneId);
      const bookingProductTripIdStr = String(booking?.bookingProductTripId);
      const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

      if (!initialOriginalAllocations[combinedKey]) {
        initialOriginalAllocations[combinedKey] = [];
      }

      if (!initialSelectedBuses[combinedKey]) {
        initialSelectedBuses[combinedKey] = {
          bookingProductTripId: booking.bookingProductTripId,
          bookingId: booking.bookingId,
          paxNo: booking.paxNo,
          allocNo: booking?.allocNo,
          zoneId: booking?.zoneId,
          allocations: [],
        };
      }

      booking.flightAllocation?.forEach((allocation: any) => {
        if (allocation.operationBalloonFlightId) {
          initialOriginalAllocations[combinedKey].push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            paxNo: Number(allocation.paxNo || booking.paxNo),
          });

          initialSelectedBuses[combinedKey].allocations.push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            splitMaleNo: Number(allocation?.male),
            splitFemaleNo: Number(allocation?.female),
            splitChildNo: Number(allocation?.child),
            oldOperationBalloonFlightId: allocation.operationBalloonFlightId,
            splitPax: Number(allocation.paxNo || booking.paxNo),
            isDeleted: false,
            maleWeight: booking?.maleWeight,
            femaleWeight: booking?.femaleWeight,
            childWeight: booking?.childWeight,
          });
        }
      });
      if (initialSelectedBuses[combinedKey].allocations.length === 0) {
        delete initialSelectedBuses[combinedKey];
      }
    });

    setOriginalAllocations(initialOriginalAllocations);
    setSelectedFlight(initialSelectedBuses);
    // }
    setEditModeMap((prev) => ({
      ...prev,
      [`${id}_${bookingProductTripId}`]: !prev[`${id}_${bookingProductTripId}`],
    }));
  };

  const cancelEditMode = (bookingId: number, bookingProductTripId: number) => {
    // Reset to original state of allocations
    const originalBusAllocs: { [operationBalloonFlightId: string]: number } =
      {};

    const originalBusAllocsWeight: {
      [operationBalloonFlightId: string]: number;
    } = {};
    flightData?.flightList?.forEach((flight: any) => {
      originalBusAllocs[flight.operationBalloonFlightId] =
        flight.allocatedPax || 0;

      originalBusAllocsWeight[flight.operationBalloonFlightId] =
        flight.totalWeight || 0;
    });
    setFlightAllocations(originalBusAllocs);
    setBusAllocationsWeight(originalBusAllocsWeight);

    // Restore original busPax count
    setRealTimeFlightPax({
      totalPax: flightData?.totalPax ?? 0,
      totalAllocatedPax: flightData?.totalAllocatedPax ?? 0,
    });

    // TODO: may be this is reason to reset value and update
    // Reset selectedFlight to original state
    const initialSelectedBuses: { [key: string]: ISelectedFlightsData } = {};
    const initialOriginalAllocations: {
      [key: string]: IOriginalAllocation[];
    } = {};

    flightData?.bookings?.forEach((booking: any) => {
      const bookingIdString = String(booking.bookingId);
      const zoneIdStr = String(booking.zoneId);
      const bookingProductTripIdStr = String(booking?.bookingProductTripId);
      const bookingIdStr = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

      if (!initialOriginalAllocations[bookingIdStr]) {
        initialOriginalAllocations[bookingIdStr] = [];
      }

      if (!initialSelectedBuses[bookingIdStr]) {
        initialSelectedBuses[bookingIdStr] = {
          bookingProductTripId: booking.bookingProductTripId,
          bookingId: booking.bookingId,
          paxNo: booking.paxNo,
          allocNo: booking?.allocNo,
          zoneId: booking?.zoneId,
          allocations: [],
        };
      }

      booking.flightAllocation?.forEach((allocation: any) => {
        if (allocation.operationBalloonFlightId) {
          initialOriginalAllocations[bookingIdStr].push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            paxNo: Number(allocation.paxNo || booking.paxNo),
          });

          initialSelectedBuses[bookingIdStr].allocations.push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            splitMaleNo: Number(allocation?.male),
            splitFemaleNo: Number(allocation?.female),
            splitChildNo: Number(allocation?.child),
            oldOperationBalloonFlightId: allocation.operationBalloonFlightId,
            splitPax: Number(allocation.paxNo || booking.paxNo),
            isDeleted: false,
            maleWeight: booking?.maleWeight,
            femaleWeight: booking?.femaleWeight,
            childWeight: booking?.childWeight,
          });
        }
      });

      if (initialSelectedBuses[bookingIdStr].allocations.length === 0) {
        delete initialSelectedBuses[bookingIdStr];
      }
    });

    setOriginalAllocations(initialOriginalAllocations);
    setSelectedFlight(initialSelectedBuses);
    setEditModeMap((prev) => ({
      ...prev,
      [`${bookingId}_${bookingProductTripId}`]: false,
    }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setSelectedDateFromStore(date);
    }
  };

  // this fun use to handle pax count
  const handlePaxCountChange = (
    bookingId: string | number,
    bookingProductTripId: number | string,
    operationBalloonFlightId: number,
    field: 'splitMaleNo' | 'splitFemaleNo' | 'splitChildNo',
    value: number | '',
    zoneId: number,
  ) => {
    const bookingIdString = String(bookingId);
    const operationBalloonFlightIdStr = String(operationBalloonFlightId);
    const zoneIdStr = String(zoneId);
    const bookingProductTripIdStr = String(bookingProductTripId);
    const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

    setSelectedFlight((prev) => {
      const currentBooking = prev[combinedKey];
      if (!currentBooking) return prev;

      // Create a deep copy of allocations
      const updatedAllocations = currentBooking.allocations.map(
        (allocation) => ({ ...allocation }),
      );

      // Find the current allocation index
      const currentAllocationIndex = updatedAllocations.findIndex(
        (a) =>
          String(a.operationBalloonFlightId) === operationBalloonFlightIdStr &&
          !a.isDeleted,
      );

      if (currentAllocationIndex === -1) return prev;

      // Get the current allocation
      const currentAllocation = updatedAllocations[currentAllocationIndex];

      // If input is cleared, store an empty string instead of 0
      const newValue = value === '' ? '' : Number(value);

      // Update the allocation
      const updatedAllocation = { ...currentAllocation, [field]: newValue };

      const newSplitPax =
        updatedAllocation?.splitMaleNo ??
        0 + updatedAllocation?.splitFemaleNo ??
        0 + updatedAllocation?.splitChildNo ??
        0;

      const updatedAllocation_SplitPax = {
        ...updatedAllocation,
        splitPax: newSplitPax,
      };

      // Update the allocations list
      updatedAllocations[currentAllocationIndex] = updatedAllocation_SplitPax;

      // Calculate total allocated pax across all allocations
      const totalAllocatedPax = updatedAllocations.reduce((sum, a) => {
        if (a.isDeleted) return sum;
        const allocMaleCount = Number(a.splitMaleNo || 0);
        const allocFemaleCount = Number(a.splitFemaleNo || 0);
        const allocChildCount = Number(a.splitChildNo || 0);
        return sum + allocMaleCount + allocFemaleCount + allocChildCount;
      }, 0);

      return {
        ...prev,
        [combinedKey]: {
          ...currentBooking,
          allocations: updatedAllocations,
          allocNo: totalAllocatedPax,
        },
      } as { [key: string]: ISelectedFlightsData };
    });
  };

  const addTemperature = async (data: IAddTemperatureRequest) => {
    try {
      const response = await operationAPI.addTemperature(data);
      showOrReplaceToast(
        'operations',
        translate('operation.tempAddedSuccessfully'),
        'success',
      );
      console.log(response, 'this is the value of the response');
    } catch (error) {
      toast.warn(translate('operation.commonError'));
    }
  };
  const [temp, setTemp] = useState<number>();
  const { selectedTemperature, setSelectedTemperature, resetTemperature } =
    useTemperatureStore();
  const [apiResponse, setApiResponse] = useState<IOperations>();
  const encodedDate = useMemo(
    () => encodeDateForUrl(selectedDate),
    [selectedDate],
  );
  const {
    data: operationDetailsData,
    isLoading: tempLoading,
    isFetching,
    refetch,
  } = useOperationDetails(selectedLocation.OwnerId, encodedDate);

  useEffect(() => {
    if (operationDetailsData) {
      setApiResponse(operationDetailsData); // ✅ update state here
    }
  }, [operationDetailsData]);

  useEffect(() => {
    refetch();
  }, []);

  const [tempList, setTempList] = useState<ITemperature[]>([]);
  const [todayTemp, setTodayTemp] = useState<number>();
  const {
    data: temperatureList,
    isLoading: tempIsLoading,
    isFetching: tempIsFetching,
    isError,
    refetch: refetchTemp,
  } = useTemperatureList();

  useEffect(() => {
    if (temperatureList) {
      setTempList(temperatureList);
    }
  }, [temperatureList]);

  const {
    data: todayTemperature,
    isLoading: todayTempIsLoading,
    isFetching: todayTempIsFetching,
    refetch: refetchTodayTemp,
  } = useTodayTemperature(selectedLocation?.OwnerId, encodedDate);

 

  useEffect(() => {
    if (todayTemperature) {
      setTemp(todayTemperature.temperatureid1);
      setTodayTemp(todayTemperature.temperatureid1);
    }
  }, [todayTemperature]);

  useEffect(() => {
    refetchTodayTemp();
  }, [selectedLocation?.OwnerId, selectedDate]);

  return (
    <div className="flex flex-col h-screen bg-background">
      <div className="overflow-y-auto flex-1">
        {/* Header and filters */}
        <div className="bg-background">
          <div className="mt-2 bg-backgroundColor-white">
            <CalendarHeader />
          </div>
          <div className="flex items-start pl-4 pr-4 pt-4">
            <span className="font-semibold text-base text-textColor">
              {translate('flightAllocation.flightAllocation')}
            </span>
          </div>
          <div className="flex justify-between items-center  gap-4 pl-4 pr-4 pt-4 rounded">
            <div
              className={`${bgColor} flex items-center space-x-1 h-[44px] w-full p-2 rounded`}
            >
              <DateSelector
                onDateChange={handleDateChange}
                initialDate={selectedDate}
                showArrows={true}
              />
            </div>
            <div
              className={`text-start  flex items-center h-[38px] rounded border border-customBorder-border  w-[30%] mr-2 ${bgColor}`}
            >
              <span>
                <div>
                  <div className="block dark:hidden">
                    <SunIcon />
                  </div>
                  <div className="hidden dark:block">
                    <SunIconDark />
                  </div>
                </div>
              </span>
              {todayTempIsLoading || todayTempIsFetching ? (
                <Loader />
              ) : (
                <ReusableSelect<ITemperature>
                  options={(tempList || []).sort(
                    (a, b) => a.temperature - b.temperature,
                  )}
                  className={`w-[80px] xl:w-full rounded p-2 text-text-primary  text-sm ${bgColor}`}
                  getOptionLabel={(option: ITemperature) =>
                    `${option.temperature}°C`
                  }
                  getOptionValue={(option: ITemperature) =>
                    option.temperatureId
                  }
                  onChange={(selectedValue: ITemperature) => {
                    setTemp(selectedValue.temperatureId);
                    setSelectedTemperature(selectedValue); // Update Zustand store
                    const data = {
                      temp1: selectedValue.temperatureId,
                      ownerId: selectedLocation.OwnerId,
                      toDate: encodeDateForUrl(selectedDate),
                    };
                    addTemperature(data);
                  }}
                  value={temp || todayTemp || undefined}
                  placeholder="Temp"
                />
              )}
            </div>
          </div>
          <div className={`${bgColor} "p-4 mt-4"`}>
            <div className="flex justify-between items-center bg-backgroundColor-tableBgColor text-orange-600 rounded p-3 mb-4 mt-4 ml-2 mr-2">
              <span>
                <div>
                  <div className="block dark:hidden">
                    <WarningIcon />
                  </div>
                  <div className="hidden dark:block">
                    <WarningIconDark />
                  </div>
                </div>
                {/* <WarningIcon /> */}
              </span>
              <span className="text-text-textColorGray">
                {unAllocatedPaxPercentage}
                {translate('flightAllocation.percentageOfUnAllocated')}
              </span>

              <button
                disabled={Number(unAllocatedPaxPercentage) <= 0}
                onClick={() => onIsUnallocatedVisible()}
                className={`border pl-4 pr-4 pt-1 pb-1 rounded ${
                  Number(unAllocatedPaxPercentage) <= 0
                    ? 'cursor-not-allowed bg-[var(--disableBtnBg)] text-[var(--disableBtnColor)] border--[var(--disableBtnBorder)]'
                    : 'text-textColor border-customBorder-border bg-backgroundColor-white'
                }`}
              >
                {translate('common.view')}
              </button>
              <UnallocatedCard
                isVisible={isUnallocatedVisible}
                setIsVisible={setIsUnallocatedVisible}
                heading="Flight with Unallocated pax"
                data={unAllocatedPax ? unAllocatedPax : []}
              />
            </div>
            <div className="text-text-textColorGray text-sm font-medium mb-2 mr-2 ml-2 flex items-center justify-between bg-backgroundColor-white h-[44px] shadow rounded-sm p-4">
              <span className="text-text-primary font-normal  text-[14px]">
                {translate('flightAllocation.flights')}
              </span>
              <span className="text-text-primary font-normal mr-4 text-[14px]">
                <span className="text-textColor">
                  {/* {flightData?.totalAllocatedPax} */}
                  {realTimeFlightPax['totalAllocatedPax']}
                </span>
                /{flightData?.totalPax}
              </span>
            </div>
          </div>
        </div>
        {/* Sticky Flight Cards */}
        <div
          className={`${bgColor} sticky top-8 z-20 ${flightData?.flightList?.length === 0 ? 'hidden' : 'grid'} grid-cols-2 gap-4 p-4`}
        >
          {flightData?.flightList?.map(
            (
              flight: {
                formattedBalloonName: string;
                allocCount: string;
                allocatedPax: number;
                balloonWeight: string;
                totalWeight: string;
                maxPax: number;
                operationBalloonFlightId: number;
                cancelled: boolean;
                pilotWeight: number;
                minPax: number;
              },
              index: number,
            ) => (
              <FlightCard
                key={index}
                flightName={flight?.formattedBalloonName}
                filledPercentage={`${Math.round((Number(flight.allocCount) / Number(flight.allocatedPax)) * 100)}`}
                // currentCount={flight.allocatedPax}
                currentCount={
                  flightAllocations[flight?.operationBalloonFlightId]
                }
                totalCount={flight?.minPax}
                totalWeight={flight?.balloonWeight}
                // filledWeight={flight.totalWeight}
                filledWeight={`${busAllocationsWeight[flight?.operationBalloonFlightId]}`}
                cancelled={flight.cancelled}
                bgColor={`bg-background`}
                pilotWeight={flight?.pilotWeight}
              />
            ),
          )}
        </div>
        {/* Bookings List */}
        <div className={`${bgColor} p-6`}>
          <div
            className={`mt-4 ${flightData?.bookings?.length === 0 ? 'h-screen' : ''}`}
          >
            {isPageLoading ? (
              <div className="inline-grid">
                <Loader width={50} height={50} />
              </div>
            ) : flightData?.bookings?.length === 0 ? (
              <div className="text-text-primary flex justify-center items-center h-80 text-lg font-medium">
                {translate('flightAllocation.noData')}
              </div>
            ) : (
              flightData?.bookings?.map(
                (
                  bookings: {
                    pickup: string;
                    dropoff: string;
                    bookingId: number;
                    agentName: string;
                    childWeight: string;
                    comment: string;
                    paxName: string;
                    earlyReturn: boolean;
                    allocNo: number;
                    paxNo: number;
                    femaleWeight: string;
                    maleWeight: string;
                    flightNo: string;
                    male: number;
                    female: number;
                    child: number;
                    status: string;
                    bookingProductTripId: number;
                    operationBalloonFlightId: number;
                    productName: string;
                    children: number;
                    zoneId: number;
                    flightAllocation: IFlightAllocation[];
                  },
                  index: number,
                ) => (
                  <Accordion
                    title={`${bookings?.paxName}: ${bookings?.male}/${bookings?.female}/${bookings?.children}:${bookings?.paxNo}`}
                    icon={
                      <div>
                        <div className="block dark:hidden">
                          <EditIcon />
                        </div>
                        <div className="hidden dark:block">
                          <EditIconDark />
                        </div>
                      </div>
                    }
                    bgColor="bg-backgroundColor-white"
                    status={`${bookings?.status}`}
                    key={index}
                    onToggleEditMode={onToggleEditMode}
                    bookingId={bookings?.bookingId}
                    bookingProductTripId={bookings?.bookingProductTripId}
                  >
                    <FlightBookingDetailsCard
                      bookingId={bookings?.bookingId}
                      bookingProductTripId={bookings?.bookingProductTripId}
                      operationBalloonFlightId={
                        bookings?.operationBalloonFlightId
                      }
                      pickup={bookings?.pickup}
                      dropoff={bookings?.dropoff}
                      id={bookings?.bookingId}
                      earlyReturn={bookings?.earlyReturn}
                      comment={bookings?.comment}
                      tour={bookings?.productName}
                      agent={bookings?.agentName}
                      allocated={bookings?.paxNo}
                      flight={bookings?.flightNo}
                      pax={bookings?.paxNo}
                      male={bookings?.male}
                      female={bookings?.female}
                      child={bookings?.children}
                      maleWeight={bookings?.maleWeight}
                      femaleWeight={bookings?.femaleWeight}
                      childWeight={bookings?.childWeight}
                      zoneId={bookings?.zoneId}
                      flights={
                        flightData?.flightList ? flightData?.flightList : []
                      }
                      selectedFlight={selectedFlight}
                      handleCheckboxChange={handleCheckboxChange}
                      handlePaxCountChange={handlePaxCountChange}
                      cancelEditMode={cancelEditMode}
                      allocatedFlight={allocatedFlight}
                      editModeMap={editModeMap}
                      bookings={bookings}
                      isLoading={isLoading}
                    />
                  </Accordion>
                ),
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightMobile;
